/**
 * Static PDP compare-at: hide Bundler's flickering original price and paint
 * list-price × quantity once onto a node Bundler does not own.
 * Display only — checkout pricing is unchanged.
 */
(function () {
  'use strict';

  const COMPARE_CLASS = 'tava-locked-compare';
  let observer = null;
  let painting = false;

  /**
   * Formats integer cents with Shopify's money formatter when available.
   * @param {number} cents
   * @returns {string}
   */
  function formatCents(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, Shopify.money_format || '${{amount}}');
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Resolves the locked "before" unit price in cents (compare-at / list, not sale).
   * @param {HTMLElement} productInfo
   * @returns {number|null}
   */
  function getCompareAtUnitCents(productInfo) {
    const fromCompare = parseInt(productInfo.dataset.compareAtUnitPrice, 10);
    if (Number.isFinite(fromCompare) && fromCompare > 0) return fromCompare;

    const fromRegular = parseInt(productInfo.dataset.regularUnitPrice, 10);
    if (Number.isFinite(fromRegular) && fromRegular > 0) return fromRegular;

    const variant =
      window.meta && window.meta.product && window.meta.product.variants
        ? window.meta.product.variants[0]
        : null;
    if (!variant) return null;
    const compare = parseInt(variant.compare_at_price, 10);
    if (Number.isFinite(compare) && compare > 0) return compare;
    const price = parseInt(variant.price, 10);
    return Number.isFinite(price) && price > 0 ? price : null;
  }

  /**
   * True when Auto Refill / Seal subscription is selected.
   * @param {HTMLElement} productInfo
   * @returns {boolean}
   */
  function isSubscribed(productInfo) {
    const box = productInfo.querySelector('.bndlr-subscription-checkbox');
    if (box) return box.checked;
    return Boolean(
      productInfo.querySelector('input[name^="subs_type_"]:not([value="one_time"]):checked')
    );
  }

  /**
   * True when a Buy 2/3 card is on the page but still missing our locked compare.
   * @param {HTMLElement} productInfo
   * @returns {boolean}
   */
  function needsStamp(productInfo) {
    const wraps = productInfo.querySelectorAll('.bndlr-radio-container .bndlr-price');
    for (let i = 0; i < wraps.length; i += 1) {
      const qtyInput = wraps[i]
        .closest('.bndlr-radio-container')
        .querySelector('input[name="bundle_quantity"]');
      const qty = parseInt(qtyInput && qtyInput.value, 10);
      if (qty > 1 && !wraps[i].querySelector('.' + COMPARE_CLASS)) return true;
    }
    return false;
  }

  /**
   * Paints (or re-attaches) the static strikethrough. The amount never changes
   * with Auto Refill — only Buy 1 visibility does.
   */
  function paintStaticCompare() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    const unitCents = getCompareAtUnitCents(productInfo);
    if (!unitCents) return;

    const subscribed = isSubscribed(productInfo);
    const tiers = productInfo.querySelectorAll('.bndlr-radio-container');
    if (!tiers.length) return;

    painting = true;
    tiers.forEach(function (tier) {
      const qtyInput = tier.querySelector('input[name="bundle_quantity"]');
      const priceWrap = tier.querySelector('.bndlr-price');
      if (!qtyInput || !priceWrap) return;

      const quantity = parseInt(qtyInput.value, 10);
      if (!Number.isFinite(quantity) || quantity < 1) return;

      // Buy 2/3 always have a list-price compare; Buy 1 only when subscribed.
      const shouldShow = quantity > 1 || subscribed;
      let el = priceWrap.querySelector('.' + COMPARE_CLASS);

      if (!shouldShow) {
        if (el) el.hidden = true;
        return;
      }

      const cents = unitCents * quantity;
      const centsStr = String(cents);
      if (!el) {
        el = document.createElement('span');
        el.className = COMPARE_CLASS;
        el.setAttribute('aria-hidden', 'true');
        priceWrap.appendChild(el);
      }

      // Write the list total only when it is missing or the variant unit changed.
      if (el.dataset.lockedCents !== centsStr) {
        el.dataset.lockedCents = centsStr;
        el.textContent = formatCents(cents);
      }
      el.hidden = false;
    });
    painting = false;
  }

  /**
   * Stores the list/compare-at unit without letting a selling-plan price shrink it.
   * @param {HTMLElement} productInfo
   * @param {object|null} variant
   */
  function setCompareAtFromVariant(productInfo, variant) {
    if (!variant) return;
    const compare = parseInt(variant.compare_at_price, 10);
    const price = parseInt(variant.price, 10);
    if (Number.isFinite(compare) && compare > 0) {
      productInfo.dataset.compareAtUnitPrice = String(compare);
      if (Number.isFinite(price) && price > 0) {
        productInfo.dataset.regularUnitPrice = String(price);
      }
      return;
    }
    if (!Number.isFinite(price) || price <= 0) return;
    productInfo.dataset.regularUnitPrice = String(price);
    const existing = parseInt(productInfo.dataset.compareAtUnitPrice, 10);
    if (!Number.isFinite(existing) || existing <= 0) {
      productInfo.dataset.compareAtUnitPrice = String(price);
    }
  }

  /**
   * True when the event came from Bundler qty radios or Auto Refill.
   * @param {EventTarget|null} target
   * @returns {boolean}
   */
  function isPurchaseOptionTarget(target) {
    if (!target || !target.closest) return false;
    return Boolean(
      target.closest('.bndlr-radio-container') ||
        target.closest('.bndlr-subscription-container') ||
        target.closest('.bndlr-checkbox-container') ||
        target.closest('.bndlr-subscription-checkbox') ||
        target.closest('.sealsubs-container')
    );
  }

  /** Re-attach our node if Bundler rebuilds a tier; ignore Bundler price text changes. */
  function observeTierMarkup(productInfo) {
    if (observer) observer.disconnect();
    observer = new MutationObserver(function () {
      if (painting) return;
      if (needsStamp(productInfo)) paintStaticCompare();
    });
    observer.observe(productInfo, { childList: true, subtree: true });
  }

  /** Re-stamp after Auto Refill without changing the compare amount. */
  function onPurchaseOptionEvent(evt) {
    if (!isPurchaseOptionTarget(evt.target)) return;
    requestAnimationFrame(paintStaticCompare);
  }

  /** Boots once product-info exists; waits until locked compares are in the DOM. */
  function init() {
    const productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (evt) {
        const variant = evt && evt.data ? evt.data.variant : null;
        setCompareAtFromVariant(productInfo, variant);
        paintStaticCompare();
      });
    }

    productInfo.addEventListener('change', onPurchaseOptionEvent, true);
    productInfo.addEventListener('click', onPurchaseOptionEvent, true);

    observeTierMarkup(productInfo);
    paintStaticCompare();

    let attempts = 0;
    const bootTimer = setInterval(function () {
      attempts += 1;
      paintStaticCompare();
      if (!needsStamp(productInfo) && productInfo.querySelector('.' + COMPARE_CLASS)) {
        clearInterval(bootTimer);
      } else if (attempts >= 50) {
        clearInterval(bootTimer);
      }
    }, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.tavaUpdatePdpComparePrices = paintStaticCompare;
})();
